import React, { useState, useCallback } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { BarChart3, TrendingUp, Calendar } from "lucide-react";

interface EnhancedFinancialChartProps {
  data: any[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  formatCurrency: (value: number) => string;
  safeT: (key: string) => string;
}

export const EnhancedFinancialChart: React.FC<EnhancedFinancialChartProps> = ({
  data,
  selectedPeriod,
  onPeriodChange,
  formatCurrency,
  safeT
}) => {
  const [chartType, setChartType] = useState<'area' | 'line'>('area');
  const [showProjection, setShowProjection] = useState(false);
  const [hoveredData, setHoveredData] = useState<any>(null);

  // Calculate projections based on trend
  const projectedData = React.useMemo(() => {
    if (!showProjection || data.length < 2) return data;

    const lastTwoMonths = data.slice(-2);
    const trend = {
      income: lastTwoMonths[1].income - lastTwoMonths[0].income,
      expenses: lastTwoMonths[1].expenses - lastTwoMonths[0].expenses
    };

    // Project next 3 months
    const projections = [];
    for (let i = 1; i <= 3; i++) {
      const lastMonth = data[data.length - 1];
      projections.push({
        month: `Future ${i}`,
        income: Math.max(0, lastMonth.income + (trend.income * i)),
        expenses: Math.max(0, lastMonth.expenses + (trend.expenses * i)),
        savings: (lastMonth.income + (trend.income * i)) - (lastMonth.expenses + (trend.expenses * i)),
        isProjected: true
      });
    }

    return [...data, ...projections];
  }, [data, showProjection]);

  const customTooltip = useCallback(({ active, payload, label }: any) => {
    if (active && payload && payload.length > 0) {
      const isProjected = payload[0]?.payload?.isProjected;
      return (
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg">
          <div className="flex items-center mb-2">
            <p className="font-semibold text-gray-900 dark:text-white">
              {label}
            </p>
            {isProjected && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                Projected
              </span>
            )}
          </div>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between mb-1">
              <span 
                className="flex items-center text-sm"
                style={{ color: entry.color }}
              >
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: entry.color }}
                />
                {entry.name}:
              </span>
              <span className="font-medium ml-4">
                {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  }, [formatCurrency]);

  const handleDataPointClick = useCallback((data: any) => {
    setHoveredData(data);
  }, []);

  return (
    <div className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border dark:border-border-dark">
        <div>
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {safeT('dashboard.monthlyOverview')}
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {safeT('dashboard.chartDescription')}
          </p>
        </div>
        <div className="text-xs px-2 py-1 rounded-full bg-secondary/10 dark:bg-primary-dark/10 text-secondary dark:text-primary-dark">
          Interactive
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">

        <div className="flex flex-wrap items-center gap-2">
          {/* Chart Type Toggle */}
          <div className="flex items-center border border-gray-200 dark:border-gray-700 rounded-lg p-1">
            <Button
              variant={chartType === 'area' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('area')}
              className="h-8 px-3"
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            <Button
              variant={chartType === 'line' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('line')}
              className="h-8 px-3"
            >
              <TrendingUp className="h-4 w-4" />
            </Button>
          </div>

          {/* Projection Toggle */}
          <Button
            variant={showProjection ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowProjection(!showProjection)}
            className="h-9"
          >
            <Calendar className="h-4 w-4 mr-1 rtl:ml-1 rtl:mr-0" />
{safeT('dashboard.forecast')}
          </Button>

          {/* Period Selector */}
          <Select value={selectedPeriod} onValueChange={onPeriodChange}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="thisMonth">{safeT('dashboard.overview.periods.thisMonth')}</SelectItem>
              <SelectItem value="lastMonth">{safeT('dashboard.overview.periods.lastMonth')}</SelectItem>
              <SelectItem value="last3Months">{safeT('dashboard.overview.periods.last3Months')}</SelectItem>
              <SelectItem value="last6Months">{safeT('dashboard.overview.periods.last6Months')}</SelectItem>
              <SelectItem value="thisYear">{safeT('dashboard.overview.periods.thisYear')}</SelectItem>
              <SelectItem value="allTime">{safeT('dashboard.overview.periods.allTime')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[400px] mt-4 bg-white/50 dark:bg-[#1E2B42]/30 rounded-xl p-4">
        {projectedData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={projectedData}
              onClick={handleDataPointClick}
            >
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F44336" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#F44336" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2196F3" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#2196F3" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(229, 231, 235, 0.4)" 
                vertical={false} 
              />
              
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return value;
                }}
              />
              
              <Tooltip content={customTooltip} />
              
              <Legend />

              {/* Reference line for break-even */}
              <ReferenceLine y={0} stroke="#666" strokeDasharray="2 2" />

              {chartType === 'area' ? (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="income" 
                    stackId="1" 
                    stroke="#4CAF50" 
                    strokeWidth={2}
                    fill="url(#incomeGradient)"
                    name={safeT('dashboard.legend.income')}
                    connectNulls={false}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stackId="2" 
                    stroke="#F44336" 
                    strokeWidth={2}
                    fill="url(#expensesGradient)"
                    name={safeT('dashboard.legend.expenses')}
                    connectNulls={false}
                  />
                </>
              ) : (
                <>
                  <Line 
                    type="monotone" 
                    dataKey="income" 
                    stroke="#4CAF50" 
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#4CAF50' }}
                    activeDot={{ r: 6, fill: '#4CAF50' }}
                    name={safeT('dashboard.legend.income')}
                    connectNulls={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="expenses" 
                    stroke="#F44336" 
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#F44336' }}
                    activeDot={{ r: 6, fill: '#F44336' }}
                    name={safeT('dashboard.legend.expenses')}
                    connectNulls={false}
                  />
                </>
              )}
              
              <Line 
                type="monotone" 
                dataKey="savings" 
                stroke="#2196F3" 
                strokeWidth={2}
                strokeDasharray="0"
                dot={{ r: 3, fill: '#2196F3' }}
                activeDot={{ r: 5, fill: '#2196F3' }}
                name={safeT('dashboard.legend.netSavings')}
                connectNulls={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">{safeT('dashboard.noDataAvailable')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Data Summary */}
      {projectedData.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {[
            { key: 'income', label: safeT('dashboard.summary.totalIncome'), color: '#4CAF50' },
            { key: 'expenses', label: safeT('dashboard.summary.totalExpenses'), color: '#F44336' },
            { key: 'savings', label: safeT('dashboard.summary.netSavings'), color: '#2196F3' }
          ].map(({ key, label, color }) => {
            const total = data.reduce((sum, month) => sum + (month[key] || 0), 0);
            return (
              <div key={key} className="bg-white/60 dark:bg-[#1E2B42]/60 rounded-lg p-3">
                <div className="flex items-center mb-1">
                  <div 
                    className="h-2 w-2 rounded-full mr-2"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
                </div>
                <p className="text-base font-semibold" style={{ color }}>
                  {formatCurrency(total)}
                </p>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}; 